import { AusverkaufAbzeichen } from '@/components/AusverkaufAbzeichen';
// src/pages/analytics/InventoryReach.tsx
// Task 17: Lager-Reichweite — die Seite, mit der der Betreiber entscheidet, was er beim
// Lieferanten nachbestellen muss (Nachlieferzeit ca. 6 Monate). Ersetzt eine manuell
// gepflegte Tabellenkalkulation mit Farbmarkierungen — deshalb hier bewusst NICHT nur
// Farbe fuer die Dringlichkeit (Icon-Form + Textlabel als zweites, farbunabhaengiges
// Signal, siehe lib/inventory-reach.ts classifyReachUrgency). Visuelle Sprache identisch
// zu den Geschwister-Seiten pages/overview/Overview.tsx (Task 15) und
// pages/analytics/ArticleAnalysis.tsx (Task 16): Panel/font-display-Header/StatusPill,
// KEIN Wechsel auf die aeltere Optik von pages/stock/StockList.tsx.
import { useEffect, useMemo, useRef, useState } from "react";
import { GrundartikelLink } from "@/components/catalog/GrundartikelLink";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  MinusCircle,
  Pencil,
  Check,
  X,
  Download,
  PackageSearch,
  Copy,
  Columns3,
} from "lucide-react";
import { fehlerText } from "@/lib/fehlerText";
import { Panel } from "@/components/Panel";
import { DataTable, type Column } from "@/components/DataTable";
import {
  REACH_SPALTEN,
  sichtbareSpalten,
  ladeVersteckteSpalten,
  speichereVersteckteSpalten,
  type ReachSpalte,
} from "@/lib/reach-spalten";
import { StockNotePopover } from "@/components/StockNotePopover";
import { StatusPill, type PillKind } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  useDashboard,
  useReach,
  useSuppliers,
  useSyncSuppliers,
  useSetNoteStatus,
  useRenameSupplier,
  usePurchaseNotes,
  useTop,
  REACH_WINDOWS,
  type ReachWindow,
  type ReachRow,
  type PurchaseNoteCloseStatus,
} from "@/api/dashboard";
import { usePurchaseOrders } from "@/api/purchase";
import { ApiError } from "@/api/client";
import { formatEuro } from "@/lib/dash-format";
import { downloadCsv } from "@/lib/csv";
import {
  classifyReachUrgency,
  formatReachMonths,
  supplierRowLabel,
  supplierOptionLabel,
  visibleSupplierOptions,
  summarizeOpenNotes,
  summarizeOpenPurchases,
  openPurchaseWarnings,
  bestelltZelle,
  formatEtaKurz,
  type BestelltZelle,
  sortiereOffeneBestellungen,
  fasseOffeneBestellungenZusammen,
  maxMonateAusWahl,
  mengeWirktAufAuswahl,
  buildReachCsv,
  headerSelectState,
  type ReachUrgency,
} from "@/lib/inventory-reach";
import { parseSkuPaste } from "@/lib/sku-list";
import { Link } from "react-router-dom";
import BestellungErfassenDialog, { type BestellZeile } from "./BestellungErfassenDialog";
import { leseOffeneBestellung } from "@/lib/reach-pending-order";

// A5: Auswahl fuer „Geht aus in <= X Monaten" — 1 bis 12, dazu „alle".
const MAX_MONTHS_OPTIONEN: readonly number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const REACH_WINDOW_LABELS: Record<ReachWindow, string> = {
  "3M": "3 Monate",
  "6M": "6 Monate",
  "12M": "12 Monate",
  "24M": "24 Monate",
};

const URGENCY_ICON: Record<ReachUrgency, typeof AlertTriangle> = {
  critical: AlertTriangle,
  warning: AlertCircle,
  ok: CheckCircle2,
  no_sales: MinusCircle,
};
const URGENCY_ICON_CLASS: Record<ReachUrgency, string> = {
  critical: "text-coral",
  warning: "text-warn",
  ok: "text-success",
  no_sales: "text-muted",
};
const URGENCY_PILL_KIND: Record<ReachUrgency, PillKind> = {
  critical: "crit",
  warning: "low",
  ok: "ok",
  no_sales: "neutral",
};

const LIMIT = 200;
// Backend-Obergrenze fuer die Nummern-Liste (routes.ts /reach/: skus max 500, limit max 500).
// Codex-Finding D: mehr Eintraege wuerden serverseitig mit 400 abgelehnt — das wird hier
// VOR dem Absenden abgefangen (Button gesperrt + Inline-Hinweis statt generischer Fehler).
const SKU_LIST_MAX = 500;

// ------------------------------------------------------------
//  Reichweite-Zelle: Icon (Form) + StatusPill (Farbe + Text) + Monatszahl. Drei
//  redundante Signale fuer dieselbe Information — Farbe ist damit nie die einzige Quelle
//  (Fachanforderung: Farbsehschwaeche + Schwarzweiss-Ausdruck).
// ------------------------------------------------------------
function UrgencyCell({ months }: { months: number | null }) {
  const info = classifyReachUrgency(months);
  const Icon = URGENCY_ICON[info.urgency];
  return (
    <div className="flex items-center gap-2">
      <Icon className={cn("size-4 shrink-0", URGENCY_ICON_CLASS[info.urgency])} aria-hidden="true" />
      <div>
        <StatusPill kind={URGENCY_PILL_KIND[info.urgency]} label={info.label} />
        <div className="text-[11px] text-ink-2 font-mono mt-0.5">{formatReachMonths(months)}</div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
//  Lieferanten-Zelle mit Inline-Pflege (Stift-Icon → Rename via useRenameSupplier).
//  State lebt in der Elternkomponente (editingSupplierId ist GLOBAL fuer die Tabelle,
//  nicht pro Zeile — mehrere Zeilen koennen denselben Lieferanten zeigen, ein Edit
//  betrifft immer den Lieferanten als Ganzes, nicht die einzelne Zeile).
// ------------------------------------------------------------
function SupplierCell({
  row,
  isEditing,
  draft,
  onDraftChange,
  onStartEdit,
  onSave,
  onCancel,
  saving,
}: {
  row: ReachRow;
  isEditing: boolean;
  draft: string;
  onDraftChange: (v: string) => void;
  onStartEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  if (row.supplier_id === null) {
    return <span className="text-ink-2">Ohne Lieferant</span>;
  }

  if (isEditing) {
    return (
      <div className="flex items-center gap-1 min-w-0">
        <Input
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSave();
            if (e.key === "Escape") onCancel();
          }}
          className="h-7 text-xs w-full min-w-0"
          autoFocus
        />
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center justify-center size-6 rounded bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 shrink-0"
          title="Speichern"
        >
          <Check className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="inline-flex items-center justify-center size-6 rounded border border-line hover:bg-panel-2 disabled:opacity-50 shrink-0"
          title="Abbrechen"
        >
          <X className="size-3.5" />
        </button>
      </div>
    );
  }

  const label = supplierRowLabel(row.supplier_id, row.supplier_name);
  return (
    <div className="group flex items-center gap-1.5 min-w-0">
      <span
        title={label}
        className={cn("truncate", label === "Unbenannter Lieferant" ? "italic text-muted-2" : "text-ink")}
      >
        {label}
      </span>
      <button
        type="button"
        onClick={onStartEdit}
        className="shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 text-muted hover:text-ink transition-opacity"
        title="Lieferant umbenennen"
        aria-label={`Lieferant „${label}" umbenennen`}
      >
        <Pencil className="size-3" />
      </button>
    </div>
  );
}

interface BuildColumnsArgs {
  windowLabel: string;
  selected: Map<number, BestellZeile>;
  loadedRows: ReachRow[];
  onToggleAll: () => void;
  onToggleRow: (row: ReachRow) => void;
  editingSupplierId: number | null;
  supplierDraft: string;
  onSupplierDraftChange: (v: string) => void;
  onStartEditSupplier: (row: ReachRow) => void;
  onSaveSupplier: () => void;
  onCancelEditSupplier: () => void;
  savingSupplier: boolean;
  orderQtyDraft: Record<number, string>;
  bestellungOffen: boolean;
  onOrderQtyDraftChange: (gaId: number, value: string) => void;
  versteckteSpalten: Set<ReachSpalte>;
  /** Plan 2026-10-26: Ziel fuer „genug bestellt" aus der Antwort (`target_months`). */
  zielMonate: number | undefined;
}

/** A7: Kopfzelle einer gekuerzten Spalte. Der kurze Text steht in der Tabelle, die volle
 *  Bedeutung als `title` (Maus) — den zugaenglichen NAMEN der Spalte traegt dagegen das
 *  `aria-label` am `<th>` (siehe `headerAriaLabel` in DataTable): ein `title` an einem
 *  nicht fokussierbaren `<span>` ist fuer Screenreader und Tastatur kein Ersatz
 *  (Gemini-Befund 4 / Codex-Befund 5). */
function SpaltenKopf({ kurz, lang }: { kurz: string; lang: string }) {
  return <span title={lang}>{kurz}</span>;
}

/** A7: Zahl in einer Spalte mit fester Breite. `table-fixed` verteilt nur die Breiten —
 *  gekappt wird hier: gekuerzt angezeigt, vollstaendig im `title`. Ohne das laufen grosse
 *  Betraege in die Nachbarzelle (Codex-Befund 1). */
function Zahl({ text }: { text: string }) {
  return (
    <div className="truncate" title={text}>
      {text}
    </div>
  );
}

/** Inhalt je Spalte. Breite, Reihenfolge und Beschriftung stehen NICHT hier, sondern in
 *  `REACH_SPALTEN` (src/lib/reach-spalten.ts) — nur dort ist das Breiten-Budget
 *  nachrechenbar, das die Zusage „kein Querscrollen bei 1600 px" traegt. */
type Zellenbau = Pick<Column<ReachRow>, "className" | "headerCell" | "cell">;

function buildColumns(args: BuildColumnsArgs): Column<ReachRow>[] {
  const zellen: Record<ReachSpalte, Zellenbau> = {
    select: {
      headerCell: () => {
        const state = headerSelectState(args.loadedRows.map((r) => r.ga_id), new Set(args.selected.keys()));
        return (
          <Checkbox
            disabled={args.bestellungOffen}
            checked={state === "all" ? true : state === "some" ? "indeterminate" : false}
            onCheckedChange={args.onToggleAll}
            aria-label="Alle geladenen Zeilen auswählen"
          />
        );
      },
      cell: (row) => (
        <Checkbox
          disabled={args.bestellungOffen}
          checked={args.selected.has(row.ga_id)}
          onCheckedChange={() => args.onToggleRow(row)}
          aria-label={`${row.name} auswählen`}
        />
      ),
    },
    dr_stock_id: {
      className: "font-mono text-xs overflow-hidden",
      cell: (row) => (
        <div className="truncate" title={row.dr_stock_id ?? undefined}>
          {row.dr_stock_id ?? "—"}
        </div>
      ),
    },
    merchant_sku: {
      className: "font-mono text-xs overflow-hidden",
      cell: (row) =>
        row.merchant_sku ? (
          <span className="flex w-full min-w-0 items-center gap-1">
            <span className="truncate" title={row.merchant_sku}>
              <GrundartikelLink code={row.merchant_sku} />
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void navigator.clipboard.writeText(row.merchant_sku!).then(
                  () => toast.success(`${row.merchant_sku} kopiert`),
                  () => toast.error("Kopieren fehlgeschlagen"),
                );
              }}
              className="shrink-0 text-muted hover:text-ink transition-colors"
              title="Sternnummer kopieren"
              aria-label={`Sternnummer ${row.merchant_sku} kopieren`}
            >
              <Copy className="size-3" />
            </button>
          </span>
        ) : ("—"),
    },
    name: {
      cell: (row) => (
        <div>
          <div className="truncate" title={row.name}>{row.name}</div>
          <AusverkaufAbzeichen {...row} />
          {!!row.bausatz_teile?.length && <div className="text-[11px] text-ink-2 whitespace-normal mt-0.5">
            Bausatz: {row.bausatz_teile.map((t, i) => <span key={t.ga_id}>{i > 0 && " + "}{t.menge}× <GrundartikelLink code={t.sku} /></span>)}
          </div>}
        </div>
      ),
    },
    supplier: {
      className: "overflow-hidden",
      cell: (row) => (
        <SupplierCell
          row={row}
          isEditing={args.editingSupplierId === row.supplier_id}
          draft={args.supplierDraft}
          onDraftChange={args.onSupplierDraftChange}
          onStartEdit={() => args.onStartEditSupplier(row)}
          onSave={args.onSaveSupplier}
          onCancel={args.onCancelEditSupplier}
          saving={args.savingSupplier}
        />
      ),
    },
    sold_window: {
      className: "text-right font-mono overflow-hidden",
      cell: (row) => <Zahl text={row.sold_window.toLocaleString("de-DE")} />,
    },
    avg_month: {
      className: "text-right font-mono overflow-hidden",
      cell: (row) => <Zahl text={row.avg_month.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} />,
    },
    quantity: {
      className: "text-right font-mono overflow-hidden",
      cell: (row) => <Zahl text={row.quantity.toLocaleString("de-DE")} />,
    },
    reach_months: {
      // Die Pille traegt den farbunabhaengigen Text („Bald nachbestellen"). Er ist
      // laenger als die Spalte — also bricht er um, statt aus der Zelle zu laufen.
      className: "whitespace-normal",
      cell: (row) => <UrgencyCell months={row.reach_months} />,
    },
    stock_value_ek: {
      className: "text-right font-mono overflow-hidden",
      cell: (row) => <Zahl text={formatEuro(row.stock_value_ek)} />,
    },
    open_notes: {
      // Einzige Spalte, die umbrechen darf: sie traegt mehrere Zeilen (Bestellung,
      // ETA/Warnung, „reicht nicht", Vormerkung). Der Rest der Tabelle bleibt einzeilig.
      // Kein align-top: die Klasse gilt auch fuer den Kopf, „Bestellt" stand sonst hoeher
      // als die anderen Spaltenkoepfe.
      className: "whitespace-normal",
      cell: (row) => {
        // Modul 38 A1: Bestellungen des Einkaufsmoduls sind die Wahrheit. Vormerkungen
        // werden nur noch als Uebergang darunter gezeigt, solange es sie gibt.
        const b = summarizeOpenPurchases(row.open_purchases);
        const warnungen = openPurchaseWarnings(row.open_purchases);
        const vormerkung = summarizeOpenNotes(row.open_notes);
        // Plan 2026-10-26: Menge vorn, Deckungsbalken, dann ob es reicht.
        const z = bestelltZelle(row, args.zielMonate);
        if (!z) {
          // Bestellung ohne offene Menge (ueberliefert, Wareneingang ohne Zuordnung): Warnung
          // und Weg in die Akte bleiben sichtbar (Astra-Befund).
          if (!b.order && warnungen.length === 0) return <span className="text-xs text-muted">—</span>;
          return (
            <div className="text-xs leading-snug" onClick={(e) => e.stopPropagation()}>
              {b.order && (
                <Link to={`/einkauf/${b.order.order_id}`} title={b.nummer} className="block truncate underline underline-offset-2">
                  {b.nummer}
                </Link>
              )}
              {warnungen.map((w) => (
                <div key={w} className="text-amber-600">{w}</div>
              ))}
            </div>
          );
        }
        return (
          <div className="text-xs leading-snug" onClick={(e) => e.stopPropagation()}>
            <BestelltKopf
              zelle={z}
              // Der Kopf verlinkt nur echte Bestellmengen; eine Vormerkung hat keine Akte.
              orderId={z.art === "bestellt" ? b.order?.order_id ?? null : null}
              nummer={b.nummer}
              eta={b.eta}
              vormerkung={vormerkung}
            />
            {z.balken && <DeckungsBalken balken={z.balken} erklaerung={z.erklaerung} />}
            {z.status.map((s) => (
              <div key={s.text} className={STATUS_TON[s.ton]}>{s.text}</div>
            ))}
            {z.art === "vorgemerkt" && b.order && (
              <Link to={`/einkauf/${b.order.order_id}`} title={b.nummer} className="block truncate underline underline-offset-2">
                {b.nummer}
              </Link>
            )}
            {warnungen.map((w) => (
              <div key={w} className="text-amber-600">{w}</div>
            ))}
            {z.zusatzVormerkung && (
              <div className="truncate text-muted" title={`Vormerkung: ${vormerkung}`}>{z.zusatzVormerkung}</div>
            )}
          </div>
        );
      },
    },
    quick_order: {
      cell: (row) => (
        <div onClick={(e) => e.stopPropagation()}>
          <input
            type="number"
            disabled={args.bestellungOffen}
            min={1}
            max={1000000}
            step={1}
            value={args.orderQtyDraft[row.ga_id] ?? ""}
            onChange={(e) => args.onOrderQtyDraftChange(row.ga_id, e.target.value)}
            placeholder="Stück"
            aria-label={`Bestellmenge ${row.sku}`}
            className="h-7 w-16 rounded border border-line bg-background px-1.5 text-xs text-ink"
          />
          {(() => { const raw = (args.orderQtyDraft[row.ga_id] ?? "").trim(); return raw !== "" && (!/^\d+$/.test(raw) || Number(raw) < 1 || Number(raw) > 1000000) ? <p role="alert" className="text-xs text-destructive">Ganzzahl 1–1.000.000 erforderlich</p> : null; })()}
        </div>
      ),
    },
    notes: {
      cell: (row) => <StockNotePopover stockItemId={row.ga_id} articleLabel={row.sku} />,
    },
  };

  return sichtbareSpalten(args.versteckteSpalten).map((platz) => {
    const bau = zellen[platz.key];
    // Nur diese eine Kopfzeile haengt vom Zustand der Seite ab: das Zeitfenster steht im
    // Filter, nicht in der Spalte — in der Kopfzeile bliebe dafuer kein Platz.
    const lang = platz.key === "sold_window" ? `Verkauft (${args.windowLabel})` : platz.lang;
    return {
      key: platz.key,
      header: platz.kurz,
      width: platz.breite ?? undefined,
      className: bau.className,
      headerCell:
        bau.headerCell ?? (platz.kurz ? () => <SpaltenKopf kurz={platz.kurz} lang={lang} /> : undefined),
      // Der zugaengliche Name ist IMMER die Langform — auch dort, wo die Kopfzeile leer
      // ist (Notiz-Spalte) oder gekuerzt („Bestellt“).
      headerAriaLabel: lang,
      cell: bau.cell,
    };
  });
}

const STATUS_TON: Record<BestelltZelle["status"][number]["ton"], string> = {
  // Dunkler als die Marken-Tokens coral/success: kleine Schrift braucht 4,5:1 Kontrast.
  fehlt: "font-medium text-red-700 dark:text-red-400",
  luecke: "font-medium text-red-700 dark:text-red-400",
  reicht: "text-green-700 dark:text-green-400",
};

/** „280 Stk bestellt" — die Menge zuerst, weil „280 · 174082026174" nicht als Stueckzahl
 *  zu lesen war. Fuehrt in die Bestellung; Nummer und ETA stehen im Namen des Links, in
 *  der 144-px-Spalte bliebe fuer sie sonst eine eigene Zeile. */
function BestelltKopf({ zelle, orderId, nummer, eta, vormerkung }: {
  zelle: BestelltZelle; orderId: number | null; nummer: string; eta: string | null; vormerkung: string;
}) {
  const inhalt = zelle.menge === null ? (
    <span className="text-ink-2">vorgemerkt, Menge offen</span>
  ) : (
    <>
      <span className="font-mono text-sm font-semibold text-ink">{zelle.menge.toLocaleString("de-DE")}</span>{" "}
      <span className="text-ink-2">Stk {zelle.art}</span>
    </>
  );
  // Nur Vormerkung: sie hat keine Akte, ihre Nummern stehen im title.
  if (orderId === null) return <div className="truncate" title={`Vormerkung: ${vormerkung}`}>{inhalt}</div>;
  const name = [
    zelle.menge === null ? "vorgemerkt, Menge offen" : `${zelle.menge.toLocaleString("de-DE")} Stk ${zelle.art}`,
    `Bestellung ${nummer}`,
    eta ? `ETA ${formatEtaKurz(eta)}` : null,
  ].filter(Boolean).join(" · ");
  return (
    <Link
      to={`/einkauf/${orderId}`}
      aria-label={name}
      title={name}
      className="block truncate decoration-ink-2/40 underline-offset-2 hover:underline"
    >
      {inhalt}
    </Link>
  );
}

/** Deckung bis zum Ziel: Bestand | unterwegs | fehlt. Die Zahlen stehen im `title`;
 *  der Balken zeigt auf einen Blick, ob die Bestellung die Luecke schliesst. */
function DeckungsBalken({ balken, erklaerung }: { balken: NonNullable<BestelltZelle["balken"]>; erklaerung: string }) {
  const pct = (n: number) => `${(n * 100).toFixed(2)}%`;
  return (
    <div
      role="img"
      aria-label={erklaerung}
      title={erklaerung}
      className="my-1 flex h-1.5 w-full overflow-hidden rounded-full bg-line-2"
    >
      <div className="h-full bg-ink-2/45" style={{ width: pct(balken.bestand) }} />
      <div className="h-full bg-primary" style={{ width: pct(balken.unterwegs) }} />
      <div className="h-full bg-coral/35" style={{ width: pct(balken.fehlt) }} />
    </div>
  );
}

/** A7: Spalten-Menue. Ausblendbar sind nur die Spalten, die `REACH_SPALTEN` dafuer
 *  freigibt — Auswahl-Haken, Nummern, Bestand, Reichweite und „Bestellen" bleiben
 *  immer stehen, sonst waere die Seite nicht mehr bedienbar. */
function SpaltenMenue({
  versteckt,
  onUmschalten,
}: {
  versteckt: Set<ReachSpalte>;
  onUmschalten: (key: ReachSpalte) => void;
}) {
  const ausblendbar = REACH_SPALTEN.filter((s) => s.ausblendbar);
  const aus = ausblendbar.filter((s) => versteckt.has(s.key)).length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <Columns3 className="size-3.5" />
          Spalten{aus > 0 ? ` (${aus} aus)` : ""}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 space-y-2">
        <p className="text-xs text-muted">
          Weniger Spalten = mehr Platz für den Namen. Die Auswahl bleibt gespeichert.
        </p>
        {ausblendbar.map((s) => (
          <label key={s.key} className="flex items-center gap-2 text-sm text-ink cursor-pointer">
            <Checkbox
              checked={!versteckt.has(s.key)}
              onCheckedChange={() => onUmschalten(s.key)}
              aria-label={s.lang}
            />
            <span title={s.lang}>{s.lang}</span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

// ------------------------------------------------------------
//  Bereich „Offene Bestellungen" — eigene, von der Reichweite-Filterleiste unabhaengige
//  Liste (usePurchaseNotes('open')), weil eine Vormerkung fuer einen Artikel offen sein
//  kann, der wegen der Filter/Blaetterung gerade gar nicht in der Tabelle sichtbar ist.
// ------------------------------------------------------------
function OpenPurchaseNotesSection() {
  const { data, isLoading, isError, refetch } = usePurchaseNotes("open");
  const setStatus = useSetNoteStatus();
  const qc = useQueryClient();

  const notes = data?.notes ?? [];

  function close(id: number, status: PurchaseNoteCloseStatus, successLabel: string) {
    setStatus.mutate(
      { id, status },
      {
        onSuccess: () => toast.success(successLabel),
        onError: (e) => {
          // Backend-Vertrag (Status-Monotonie, service.ts): ein Statuswechsel greift NUR
          // auf noch offene Vormerkungen — ist sie zwischenzeitlich (z. B. in einem
          // anderen Browser-Tab) bereits abgeschlossen worden, antwortet die API mit 409.
          // Das ist kein technischer Defekt, sondern ein erwartbarer Konflikt bei
          // gleichzeitiger Bearbeitung — deshalb hier eine eigene, verstaendliche
          // Meldung statt der rohen HTTP-Fehlermeldung, plus sofortiges Nachladen, damit
          // die Liste den echten (bereits abgeschlossenen) Zustand zeigt.
          if (e instanceof ApiError && e.status === 409) {
            toast.error("Diese Vormerkung wurde inzwischen bereits abgeschlossen — Liste wird aktualisiert.");
            void qc.invalidateQueries({ queryKey: ["dashboard"] });
          } else {
            toast.error(e instanceof ApiError ? e.message : "Status konnte nicht geändert werden.");
          }
        },
      },
    );
  }

  return (
    <Panel
      title="Offene Bestellungen"
      subtitle={
        // Keine Daten (noch nicht da oder Abruf gescheitert) -> keine Aussage. Sonst
        // stuende hier „0 Vormerkungen ohne Wareneingang", waehrend der Rumpf darunter
        // „nicht erreichbar" meldet: eine Zahl ueber etwas, das nie gemessen wurde.
        // (Befund Reviewer 2, MAJOR — am Code nachgeprueft.)
        data && !isError
          ? `${notes.length} Vormerkung${notes.length === 1 ? "" : "en"} ohne Wareneingang`
          : undefined
      }
    >
      {isError ? (
        <div className="text-sm text-coral py-4 flex items-center gap-2">
          Offene Bestellungen nicht erreichbar.
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Erneut versuchen
          </Button>
        </div>
      ) : isLoading && notes.length === 0 ? (
        <div className="text-sm text-muted py-4">Lädt…</div>
      ) : notes.length === 0 ? (
        <div className="text-sm text-muted py-4">Keine offenen Bestellvormerkungen.</div>
      ) : (
        <ul className="divide-y divide-line-2">
          {notes.map((n) => {
            const pending = setStatus.isPending && setStatus.variables?.id === n.id;
            return (
              <li key={n.id} className="flex flex-wrap items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                <div className="flex-1 min-w-[220px]">
                  <div className="text-sm text-ink">
                    <span className="font-mono"><GrundartikelLink code={n.merchant_sku ?? n.sku}>{n.merchant_sku ?? n.sku}</GrundartikelLink></span> — {n.name}
                  </div>
                  <div className="text-[11px] text-ink-2">
                    Bestellnr. <span className="font-mono">{n.order_number}</span>
                    {n.quantity !== null && ` · ${n.quantity.toLocaleString("de-DE")} Stk.`}
                    {" · erfasst "}
                    {new Date(n.created_at).toLocaleDateString("de-DE")}
                    {n.created_by && ` von ${n.created_by}`}
                    {n.note && ` · „${n.note}"`}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pending}
                    onClick={() => close(n.id, "received", `${n.order_number} als eingetroffen markiert`)}
                  >
                    Eingetroffen
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={pending}
                    onClick={() => close(n.id, "cancelled", `${n.order_number} storniert`)}
                  >
                    Stornieren
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

// ------------------------------------------------------------
//  Modul 38 A8 — Panel „Offene Bestellungen" (Einkaufsmodul, B4).
//  Fragt gezielt die zwei offenen Status ab; der Server filtert (A8-Backend), damit
//  das Panel nicht die ganze Bestell-Liste laden und selbst sieben muss.
// ------------------------------------------------------------
function OffeneBestellungenPanel() {
  const { data, isLoading, isError, refetch } = usePurchaseOrders(["bestellt", "in_abwicklung"]);
  const orders = sortiereOffeneBestellungen(data?.orders ?? []);
  const summe = fasseOffeneBestellungenZusammen(orders);

  return (
    <Panel
      title="Offene Bestellungen"
      subtitle={
        // Wie oben: ohne Daten keine Aussage. „keine offene Bestellung" ist eine Auskunft
        // ueber den Einkauf, die ein gescheiterter Abruf nicht deckt.
        !data || isError
          ? undefined
          : summe.anzahl === 0
            ? "keine offene Bestellung"
            : `${summe.anzahl} Bestellung${summe.anzahl === 1 ? "" : "en"} · ` +
              `${summe.positionen} Position${summe.positionen === 1 ? "" : "en"} · ` +
              `${summe.menge.toLocaleString("de-DE")} Stück`
      }
      bodyClassName="p-0"
    >
      {isError ? (
        <div className="text-sm text-coral py-8 text-center flex flex-col items-center gap-2">
          Bestellungen nicht erreichbar.
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Erneut versuchen
          </Button>
        </div>
      ) : isLoading && orders.length === 0 ? (
        <div className="text-sm text-muted py-8 text-center">Lädt…</div>
      ) : orders.length === 0 ? (
        <div className="text-sm text-muted py-8 text-center">
          Keine offene Bestellung. Artikel auswählen und „Bestellung erfassen" nutzen.
        </div>
      ) : (
        <ul className="divide-y divide-line-2">
          {orders.map((o) => (
            <li key={o.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
              <div className="flex-1 min-w-[220px]">
                <Link to={`/einkauf/${o.id}`} className="text-sm text-ink font-mono underline underline-offset-2">
                  {o.order_number}
                </Link>
                <span className="text-sm text-ink-2"> · {o.supplier_name ?? "ohne Lieferant"}</span>
                <div className="text-[11px] text-ink-2">
                  {o.status === "in_abwicklung" ? "in Abwicklung" : "bestellt"}
                  {o.item_count !== undefined && ` · ${o.item_count} Position${o.item_count === 1 ? "" : "en"}`}
                  {o.qty_total !== undefined && ` · ${o.qty_total.toLocaleString("de-DE")} Stück`}
                </div>
              </div>
              <div className="text-xs text-ink-2 shrink-0 tabular-nums">
                {o.next_eta ? `ETA ${new Date(o.next_eta).toLocaleDateString("de-DE")}` : "kein Termin"}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

export default function InventoryReach() {
  // Task 13: Lagerwarnungen (system/dashboard-Alerts) von der Overview hierher umgezogen —
  // die Warnliste gehoert fachlich zur Nachbestell-Entscheidung, nicht zur Verkaufsuebersicht.
  const { data: sysDashboard } = useDashboard();

  // ---- Filter ----
  const [windowMonths, setWindowMonths] = useState<ReachWindow>("6M");
  const [supplier, setSupplier] = useState<number | "none" | null>(null);
  // A5: feste Auswahl 1–12 Monate statt freier Zahl. Voreinstellung 6 — das ist die
  // Nachlieferzeit und damit der Zeitraum, den die Liste beantworten soll. Vorher stand
  // hier "alle": die Liste zeigte also standardmaessig auch Artikel, die erst in Jahren
  // leerlaufen, und verduennte damit genau die Zeilen, um die es geht.
  const [maxMonthsWahl, setMaxMonthsWahl] = useState<string>("6");
  const [category, setCategory] = useState<number | null>(null);
  const [qInput, setQInput] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [excludeOrdered, setExcludeOrdered] = useState(true);
  // GELÖSCHT*/#AUSVERKAUF: per Default ausgeblendet (Bestand 0, verstopfen sonst die
  // Spitze der Dringlichkeitsliste) — bewusst einblendbar (Nutzer-Anforderung 2026-07-21).
  const [includeDead, setIncludeDead] = useState(false);
  const [offset, setOffset] = useState(0);
  // Korrektur #13: Massen-Abfrage — eingefügte Nummern-Liste (Panel „Liste prüfen").
  const [skuListInput, setSkuListInput] = useState("");
  const [skusFilter, setSkusFilter] = useState<string[] | null>(null);

  // Welle 2 (#15a): Inline-Bestellmengen je Zeile (key = ga_id).
  // Modul 38 A8: Die Menge speist jetzt ausschliesslich die Auswahl fuer den
  // Bestell-Dialog. Der frühere Plus-Knopf daneben legte eine VORMERKUNG an — ein
  // zweites, paralleles Bestellwesen neben dem Einkaufsmodul. Genau das faellt mit A8;
  // es gibt nur noch einen Weg zur Bestellung.
  // A7: welche Spalten der Nutzer ausgeblendet hat. Der Merkposten liegt im Browser —
  // eine Sichteinstellung gehoert nicht in die Datenbank und nicht in den Vertrag.
  // Nicht nur `getItem` kann werfen — in abgeschotteten Umgebungen (privater Modus,
  // gesperrter Speicher, Sandbox-Origin) wirft bereits der Zugriff auf die Eigenschaft
  // selbst. Ungefangen im useState-Initialisierer haette das die ganze Seite gekostet
  // (Gemini-Befund 5 / Codex-Befund 6).
  const speicher = (): Storage | null => {
    try {
      return typeof window === "undefined" ? null : window.localStorage;
    } catch {
      return null;
    }
  };
  const [versteckteSpalten, setVersteckteSpalten] = useState<Set<ReachSpalte>>(() =>
    ladeVersteckteSpalten(speicher()),
  );
  function spalteUmschalten(key: ReachSpalte) {
    const neu = new Set(versteckteSpalten);
    if (neu.has(key)) neu.delete(key);
    else neu.add(key);
    setVersteckteSpalten(neu);
    // Bewusst hier und nicht im Zustands-Updater: der muss frei von Nebenwirkungen
    // bleiben (React ruft ihn im Strict Mode zweimal auf).
    speichereVersteckteSpalten(speicher(), neu);
  }

  const [orderQtyDraft, setOrderQtyDraft] = useState<Record<number, string>>({});
  const [bestellungOffen, setBestellungOffen] = useState(() => leseOffeneBestellung() !== null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const maxMonths = useMemo(() => maxMonateAusWahl(maxMonthsWahl), [maxMonthsWahl]);

  // ---- Auswahl (Map statt Set: traegt die Anzeige-Daten fuer den Dialog mit, bleibt
  //      damit auch fuer Artikel gueltig, die auf einer anderen Seite selektiert wurden
  //      und aktuell nicht geladen sind) ----
  // Modul 38: die Auswahl traegt zusaetzlich den Lieferanten — der Bestell-Dialog
  // gruppiert danach, und die Auswahl ueberlebt das Blaettern (die Zeile ist dann
  // nicht mehr geladen, ihr Lieferant muss aber bekannt bleiben).
  const [selected, setSelected] = useState<Map<number, BestellZeile>>(new Map());
  const selectedRef = useRef(selected);
  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  // Auswahl bleibt beim reinen Blaettern (Offset-Wechsel) erhalten. Aendert sich dagegen
  // eine der eigentlichen Filterbedingungen, ist die zuvor sichtbare Ergebnismenge nicht
  // mehr dieselbe — die Auswahl wird dann SICHTBAR (Toast) zurueckgesetzt, statt still zu
  // "hängen" und ggf. Artikel zu betreffen, die der Operator gar nicht mehr sieht.
  const filtersSignature = JSON.stringify([windowMonths, supplier, maxMonths, category, debouncedQ, excludeOrdered, includeDead, skusFilter]);
  const prevSignatureRef = useRef(filtersSignature);
  useEffect(() => {
    if (prevSignatureRef.current === filtersSignature) return;
    prevSignatureRef.current = filtersSignature;
    setOffset(0);
    if (selectedRef.current.size > 0) {
      toast.info(`Auswahl (${selectedRef.current.size}) wurde durch die Filteränderung zurückgesetzt.`);
      setSelected(new Map());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersSignature]);

  function toggleRow(row: ReachRow) {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.ga_id)) {
        next.delete(row.ga_id);
      } else {
        next.set(row.ga_id, { ga_id: row.ga_id, sku: row.sku, name: row.name,
          supplier_id: row.supplier_id, supplier_name: row.supplier_name,
          merchant_sku: row.merchant_sku, bausatz_teile: row.bausatz_teile });
      }
      return next;
    });
  }

  // Korrektur #14: Master-Checkbox — wirkt NUR auf die aktuell geladenen Zeilen.
  // Auswahl-Eintraege anderer Seiten (Blaetterung) bleiben unangetastet.
  function toggleAll() {
    setSelected((prev) => {
      const loadedIds = rows.map((r) => r.ga_id);
      const state = headerSelectState(loadedIds, new Set(prev.keys()));
      const next = new Map(prev);
      if (state === "all") {
        for (const id of loadedIds) next.delete(id);
      } else {
        for (const row of rows) {
          next.set(row.ga_id, { ga_id: row.ga_id, sku: row.sku, name: row.name,
          supplier_id: row.supplier_id, supplier_name: row.supplier_name,
          merchant_sku: row.merchant_sku, bausatz_teile: row.bausatz_teile });
        }
      }
      return next;
    });
  }

  // A11: Wer eine Menge eintippt, will bestellen — das Haekchen folgt der Menge.
  // Das Haekchen von Hand zu setzen bleibt moeglich; diese Funktion greift NUR bei
  // Aenderungen im Mengenfeld. Zwischenzustaende beim Tippen ("-", "abc") lassen die
  // Auswahl bewusst unberuehrt, damit sie nicht flackert.
  function onMengeGeaendert(gaId: number, value: string): void {
    setOrderQtyDraft((d) => ({ ...d, [gaId]: value }));
    const wirkung = mengeWirktAufAuswahl(value);
    if (wirkung === "unveraendert") return;
    const row = rows.find((r) => r.ga_id === gaId);
    if (!row) return;
    setSelected((prev) => {
      const next = new Map(prev);
      if (wirkung === "anhaken") {
        next.set(gaId, { ga_id: row.ga_id, sku: row.sku, name: row.name,
          supplier_id: row.supplier_id, supplier_name: row.supplier_name,
          merchant_sku: row.merchant_sku, bausatz_teile: row.bausatz_teile });
      } else {
        next.delete(gaId);
      }
      return next;
    });
  }

  // ---- Daten ----
  // Fix-Runde 1 (#13-Review): bei aktiver Nummern-Liste (bis 500 Eintraege) muss EINE Seite
  // die gesamte Liste abdecken — sonst vergleicht der „Nicht gefunden"-Abgleich unten die
  // Liste nur gegen die ersten 200 geladenen Zeilen und meldet auf Folgeseiten getroffene
  // Artikel faelschlich als fehlend. Das Backend erlaubt limit bis 500 (routes.ts /reach/).
  const effectiveLimit = skusFilter !== null ? Math.min(SKU_LIST_MAX, Math.max(LIMIT, skusFilter.length)) : LIMIT;
  // Ab wann sagt die Seite, dass die Abfrage laenger braucht?
  //
  // Die Abfrage ist auch im gesunden Fall langsam (rund 22 s bei einem grossen Lieferanten,
  // gemessen 2026-08-18). Wer 20 s lang nur „Lädt…" sieht, haelt die Seite fuer kaputt und
  // laedt neu — was die teure Abfrage ein zweites Mal startet. Der Hinweis kommt deshalb
  // frueh (5 s: laenger als jede gesunde Seite dieses Bildschirms braucht) und nennt bewusst
  // KEINE Dauer: eine Zusage wie „noch etwa 20 Sekunden" waere geraten, und sie waere in dem
  // Moment falsch, in dem der Server wieder schwaechelt.
  const LANGE_ABFRAGE_MS = 5_000;

  const reach = useReach({
    window: windowMonths,
    supplier,
    exclude_ordered: excludeOrdered,
    include_dead: includeDead,
    max_months: maxMonths,
    category,
    q: debouncedQ || null,
    limit: effectiveLimit,
    offset,
    skus: skusFilter,
  });
  const rows = reach.data?.rows ?? [];
  const total = reach.data?.total ?? 0;

  /**
   * Der Satz zum fehlgeschlagenen Abruf — oder `null`, solange nichts fehlgeschlagen ist.
   *
   * `fehlerText` nimmt den Satz des Servers, wenn es einen gibt, und faellt sonst auf den
   * eigenen zurueck. Ein 500 aus der Reichweiten-Abfrage traegt in aller Regel KEINEN
   * brauchbaren Satz (der Server bricht mitten in der Abfrage ab), aber ein 400 aus einer
   * abgelehnten Parameterkombination tut es — und dann soll der Grund dastehen.
   */
  const reachFehler = reach.isError
    ? fehlerText(reach.error, "Die Lager-Reichweite konnte nicht geladen werden.")
    : null;

  // Laeuft die Abfrage schon so lange, dass man es sagen sollte?
  //
  // Gebunden an `isFetching`, nicht an `isLoading`: auch ein Nachladen (Filterwechsel,
  // „Erneut versuchen") dauert lang, und dort ist die Frage des Nutzers dieselbe.
  //
  // Zusaetzlich gebunden an den Abfrage-Schluessel: „laeuft" allein reicht nicht. Wechselt
  // der Nutzer Filter oder Seite, waehrend die alte Abfrage noch laeuft, bleibt
  // `isFetching` durchgehend wahr — der Effekt liefe nicht neu, und der Zeitgeber der
  // ALTEN Abfrage feuerte fuer die NEUE (oder ein bereits stehender Hinweis wanderte
  // unbesehen mit hinueber). Jede Abfrage bekommt ihre eigene Uhr.
  // (Befund Reviewer 1, MINOR — am Code nachgeprueft.)
  //
  // Das Ruecksetzen im Vorlauf ist die EINZIGE Stelle, die den Hinweis wieder zurueckholt —
  // die Anzeige unten fragt nur den Zustand ab. Eine zweite Bedingung am Text (`&&
  // reach.isFetching`) stand hier zuerst und wurde entfernt: kein Test konnte sie von
  // dieser Stelle unterscheiden, sie haette also nur so ausgesehen, als sichere sie etwas.
  const reachSchluessel = `${filtersSignature}|${offset}|${effectiveLimit}`;
  const [dauertLaenger, setDauertLaenger] = useState(false);
  useEffect(() => {
    setDauertLaenger(false);
    if (!reach.isFetching) return;
    const uhr = setTimeout(() => setDauertLaenger(true), LANGE_ABFRAGE_MS);
    return () => clearTimeout(uhr);
  }, [reach.isFetching, reachSchluessel]);

  const suppliersQuery = useSuppliers();
  const suppliers = suppliersQuery.data?.suppliers ?? [];

  // Abschluss-Review, IMPORTANT-Finding: GET /dashboard/suppliers/ ist rein lesend, der
  // DR-Lieferanten-ID-Abgleich laeuft jetzt ueber POST /dashboard/suppliers/sync/ (Scope
  // stock:write). Best-effort einmal beim Laden der Seite ausloesen — ein Nutzer mit reinem
  // Lese-Scope bekommt hier 403, die Seite bleibt aber voll funktionsfaehig (bekannte
  // Lieferanten laden weiterhin ganz normal über useSuppliers()), nur ganz neue
  // DR-Lieferanten-IDs erscheinen dann erst, sobald ein Nutzer mit Schreibrecht die Seite
  // einmal geöffnet hat. Kein Toast/keine Fehlerbehandlung — bewusst unauffällig im Hintergrund.
  const syncSuppliers = useSyncSuppliers();
  const syncSuppliersRef = useRef(syncSuppliers.mutate);
  syncSuppliersRef.current = syncSuppliers.mutate;
  useEffect(() => {
    syncSuppliersRef.current(undefined, { onError: () => {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kategorie-Filter: es gibt keinen eigenen "Kategorieliste"-Endpunkt — wiederverwendet
  // wird /dashboard/top/?dim=category (Task 7/16), das die Wurzel-Kategorien (max. 25,
  // absteigend nach Menge) liefert. Der Zeitraum hier (90T) beeinflusst NUR die
  // Sortierung der Optionen, nicht welche Kategorien ueberhaupt erscheinen (LEFT JOIN im
  // Backend zeigt alle Wurzelordner unabhaengig vom Zeitfenster) — siehe Report.
  const categoryOptionsQuery = useTop("category", "90T", "qty");
  const categoryOptions = categoryOptionsQuery.data?.rows ?? [];

  // ---- Lieferanten-Inline-Pflege ----
  const renameSupplier = useRenameSupplier();
  const [editingSupplierId, setEditingSupplierId] = useState<number | null>(null);
  const [supplierDraft, setSupplierDraft] = useState("");

  function startEditSupplier(row: ReachRow) {
    if (row.supplier_id === null) return;
    setEditingSupplierId(row.supplier_id);
    setSupplierDraft(row.supplier_name ?? "");
  }
  function cancelEditSupplier() {
    setEditingSupplierId(null);
    setSupplierDraft("");
  }
  async function saveSupplier() {
    if (editingSupplierId === null) return;
    const trimmed = supplierDraft.trim();
    if (trimmed === "") {
      toast.error("Lieferantenname darf nicht leer sein.");
      return;
    }
    try {
      await renameSupplier.mutateAsync({ id: editingSupplierId, patch: { name: trimmed } });
      toast.success("Lieferant umbenannt");
      cancelEditSupplier();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Umbenennen fehlgeschlagen");
    }
  }

  // ---- Bestell-Dialog ----
  const [dialogOpen, setDialogOpen] = useState(false);
  const selectedItems = useMemo(() => Array.from(selected.values()), [selected]);

  // ---- CSV-Export der geladenen (aktuell sichtbaren) Zeilen ----
  function exportCsv() {
    const csv = buildReachCsv(rows);
    downloadCsv(`lager-reichweite_${new Date().toISOString().slice(0, 10)}.csv`, csv);
  }

  // Korrektur #16: zweiter Export NUR der ausgewaehlten (geladenen) Zeilen — nutzt
  // denselben CSV-Builder, gefiltert auf die aktuelle Auswahl.
  function exportSelectedCsv() {
    const selectedRows = rows.filter((r) => selected.has(r.ga_id));
    const csv = buildReachCsv(selectedRows);
    downloadCsv(`lager-reichweite_auswahl_${new Date().toISOString().slice(0, 10)}.csv`, csv);
  }

  const columns = useMemo(
    () =>
      buildColumns({
        windowLabel: REACH_WINDOW_LABELS[windowMonths],
        selected,
        loadedRows: rows,
        onToggleAll: toggleAll,
        onToggleRow: toggleRow,
        editingSupplierId,
        supplierDraft,
        onSupplierDraftChange: setSupplierDraft,
        onStartEditSupplier: startEditSupplier,
        onSaveSupplier: () => void saveSupplier(),
        onCancelEditSupplier: cancelEditSupplier,
        savingSupplier: renameSupplier.isPending,
        orderQtyDraft,
        bestellungOffen,
        onOrderQtyDraftChange: (gaId, value) => onMengeGeaendert(gaId, value),
        versteckteSpalten,
        zielMonate: reach.data?.target_months,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      windowMonths,
      selected,
      rows,
      editingSupplierId,
      supplierDraft,
      renameSupplier.isPending,
      orderQtyDraft,
      bestellungOffen,
      versteckteSpalten,
      reach.data?.target_months,
    ],
  );

  return (
    <div className="p-8 space-y-5 max-w-[1600px]">
      <header>
        <h1 className="font-display font-bold text-2xl text-ink">Lager-Reichweite</h1>
        <p className="text-sm text-ink-2 mt-1">
          Welche Grundartikel innerhalb der Nachlieferzeit (ca. {REACH_WINDOW_LABELS["6M"]}) leerlaufen — Grundlage
          für die Nachbestellung beim Lieferanten
        </p>
      </header>

      {(sysDashboard?.alerts ?? []).length > 0 && (
        <Panel
          title="Lagerwarnungen"
          subtitle={`${(sysDashboard?.alerts ?? []).filter((a) => a.level === "critical").length} kritisch · ${(sysDashboard?.alerts ?? []).filter((a) => a.level === "low").length} niedrig`}
        >
          <ul className="divide-y divide-line-2">
            {(sysDashboard?.alerts ?? []).map((a) => (
              <li key={a.sku} className="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0">
                <span className={`size-2 rounded-full mt-1.5 shrink-0 ${a.level === "critical" ? "bg-coral" : "bg-warn"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-ink font-medium truncate">{a.name}</div>
                  <div className="text-[11px] text-ink-2 font-mono">{a.sku} · Alarm ab {a.quantity_alert}</div>
                </div>
                <div className="text-sm font-mono text-ink shrink-0 w-16 text-right">{a.stock}</div>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <Panel title="Filter" bodyClassName="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <label className="text-xs text-ink-2 uppercase tracking-wide" htmlFor="reach-window">
              Ø-Basis (Absatz)
            </label>
            <select
              id="reach-window"
              value={windowMonths}
              onChange={(e) => setWindowMonths(e.target.value as ReachWindow)}
              className="text-xs rounded border border-line bg-background text-ink px-2 py-1.5 h-8"
            >
              {REACH_WINDOWS.map((w) => (
                <option key={w} value={w}>
                  {REACH_WINDOW_LABELS[w]}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-ink-2 uppercase tracking-wide" htmlFor="reach-supplier">
              Lieferant
            </label>
            <select
              id="reach-supplier"
              value={supplier === null ? "" : String(supplier)}
              onChange={(e) => {
                const v = e.target.value;
                setSupplier(v === "" ? null : v === "none" ? "none" : Number(v));
              }}
              className="text-xs rounded border border-line bg-background text-ink px-2 py-1.5 h-8 max-w-[220px]"
            >
              <option value="">Alle Lieferanten</option>
              <option value="none">Ohne Lieferant</option>
              {visibleSupplierOptions(suppliers, supplier).map((s) => (
                <option key={s.id} value={s.id}>
                  {supplierOptionLabel(s)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-ink-2 uppercase tracking-wide" htmlFor="reach-category">
              Kategorie
            </label>
            <select
              id="reach-category"
              value={category === null ? "" : String(category)}
              onChange={(e) => setCategory(e.target.value === "" ? null : Number(e.target.value))}
              className="text-xs rounded border border-line bg-background text-ink px-2 py-1.5 h-8 max-w-[220px]"
            >
              <option value="">Alle Kategorien</option>
              {categoryOptions.map((c) => (
                <option key={c.id ?? c.label} value={c.id ?? ""}>
                  {c.label || "Ohne Kategorie"}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-ink-2 uppercase tracking-wide" htmlFor="reach-max-months">
              Geht aus in ≤ X Monaten
            </label>
            <select
              id="reach-max-months"
              value={maxMonthsWahl}
              onChange={(e) => setMaxMonthsWahl(e.target.value)}
              className="text-xs rounded border border-line bg-background text-ink px-2 py-1.5 h-8 w-24"
            >
              {MAX_MONTHS_OPTIONEN.map((m) => (
                <option key={m} value={String(m)}>
                  {m} {m === 1 ? "Monat" : "Monate"}
                </option>
              ))}
              <option value="alle">alle</option>
            </select>
          </div>

          <div className="space-y-1.5 flex-1 min-w-[200px]">
            <label className="text-xs text-ink-2 uppercase tracking-wide" htmlFor="reach-search">
              Suche
            </label>
            <Input
              id="reach-search"
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="SKU, Sternnummer oder Name…"
              className="h-8 text-xs"
            />
          </div>

          <label
            className="flex items-center gap-2 text-xs text-ink-2 h-8 pb-0.5"
            title={`Blendet nur aus, wenn Bestand + offene Bestellung ${reach.data?.target_months ?? 6} Monate decken und vor der nächsten Ankunft nichts leerläuft. Zu wenig Bestelltes bleibt sichtbar.`}
          >
            <Checkbox checked={excludeOrdered} onCheckedChange={(v) => setExcludeOrdered(v === true)} />
            Ausreichend bestellte ausblenden
          </label>

          <label className="flex items-center gap-2 text-xs text-ink-2 h-8 pb-0.5">
            <Checkbox checked={includeDead} onCheckedChange={(v) => setIncludeDead(v === true)} />
            Gelöschte/Ausverkauf anzeigen
          </label>
        </div>
      </Panel>

      <Panel title="Liste prüfen" subtitle="Mehrere Nummern aus Excel einfügen — Reichweite für genau diese Artikel" bodyClassName="p-4">
        {(() => {
          const parsedSkus = parseSkuPaste(skuListInput);
          const tooMany = parsedSkus.length > SKU_LIST_MAX;
          return (
            <>
              <div className="flex flex-col sm:flex-row gap-3">
                <textarea
                  value={skuListInput}
                  onChange={(e) => setSkuListInput(e.target.value)}
                  placeholder={"DR-9810\n*811\n…"}
                  rows={4}
                  className="flex-1 text-xs font-mono rounded border border-line bg-background text-ink px-2 py-1.5 resize-y"
                  aria-label="Nummern-Liste (SKU, Sternnummer oder Lagernummer, eine pro Zeile)"
                />
                <div className="flex sm:flex-col gap-2 shrink-0">
                  <Button size="sm" disabled={parsedSkus.length === 0 || tooMany}
                    onClick={() => setSkusFilter(parsedSkus)}>
                    Prüfen ({parsedSkus.length})
                  </Button>
                  <Button variant="ghost" size="sm" disabled={skusFilter === null}
                    onClick={() => { setSkusFilter(null); setSkuListInput(""); }}>
                    Zurücksetzen
                  </Button>
                </div>
              </div>
              {tooMany && (
                <p className="text-[11px] text-ink-2 mt-2">
                  Maximal {SKU_LIST_MAX} Nummern pro Abfrage (aktuell {parsedSkus.length.toLocaleString("de-DE")}).
                </p>
              )}
            </>
          );
        })()}
        <p className="text-[11px] text-ink-2 mt-2">
          Eingaben müssen Lagernummer, Grundartikel-SKU oder Sternnummer sein — Kind-SKUs einzelner
          Varianten werden nicht auf die Familie aufgelöst und erscheinen als „nicht gefunden".
        </p>
        {/* `!reachFehler`: nach einem Fehlschlag ist `rows` leer — ohne diese Bedingung
            haette die Seite JEDE eingegebene Nummer als „Nicht gefunden" gemeldet, obwohl
            gar nicht gesucht wurde. Eine falsche Auskunft ist schlimmer als keine. */}
        {skusFilter !== null && !reach.isLoading && !reachFehler && (() => {
          // Restfall trotz effectiveLimit (z.B. eine Sternnummer trifft viele Grundartikel und
          // total uebersteigt 500): Abgleich waere unvollstaendig → Chips unterdruecken statt
          // falsche „Nicht gefunden"-Meldungen zu zeigen.
          if (rows.length < total) {
            return (
              <div className="mt-2 text-xs text-ink-2">
                Treffermenge größer als eine Seite — „Nicht gefunden"-Anzeige nicht verfügbar.
              </div>
            );
          }
          const found = new Set(rows.flatMap((r) => [r.sku, r.merchant_sku ?? "", r.dr_stock_id ?? ""]));
          const missing = skusFilter.filter((s) => !found.has(s));
          return missing.length > 0 ? (
            <div className="mt-2 text-xs text-coral">
              Nicht in dieser Filterauswahl ({missing.length}): <span className="font-mono">{missing.join(", ")}</span>
            </div>
          ) : null;
        })()}
      </Panel>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-ink-2">
          {/* Reihenfolge ist Absicht: der Fehlschlag steht VOR der Zahl. Ohne ihn stand hier
              „0 Grundartikel gefunden" — eine Aussage ueber den Lagerbestand, die niemand
              gemessen hat. Nichts zu wissen ist etwas anderes als nichts zu finden. */}
          {reachFehler
            ? "Nicht geladen"
            : reach.isLoading && rows.length === 0
              ? "Lädt…"
              : `${total.toLocaleString("de-DE")} Grundartikel gefunden`}
          {selected.size > 0 && (
            <>
              {" · "}
              <span className="font-medium text-ink">{selected.size} ausgewählt</span>
              {" · "}
              <button type="button" disabled={bestellungOffen} className="text-primary hover:underline disabled:opacity-50" onClick={() => setSelected(new Map())}>
                Auswahl aufheben
              </button>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <SpaltenMenue versteckt={versteckteSpalten} onUmschalten={spalteUmschalten} />
          {/* `reachFehler` sperrt mit: nach einem gescheiterten Nachladen kann `rows` noch
              die letzte erfolgreiche Antwort tragen, waehrend die Seite „Nicht geladen"
              sagt und die Tabelle ausblendet. Etwas herauszugeben, das man dem Nutzer
              gerade nicht zeigt, waere die schlechteste Auskunft von beiden.
              (Befund Reviewer 2, MAJOR — am Code nachgeprueft.) */}
          <Button variant="outline" size="sm" className="gap-1.5" onClick={exportCsv}
            disabled={Boolean(reachFehler) || rows.length === 0}>
            <Download className="size-3.5" />
            CSV exportieren (geladene Zeilen)
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={exportSelectedCsv} title="Exportiert nur ausgewählte Zeilen der aktuell geladenen Seite."
            disabled={Boolean(reachFehler) || rows.filter((r) => selected.has(r.ga_id)).length === 0}>
            <Download className="size-3.5" />
            CSV (Auswahl dieser Seite)
          </Button>
          <Button size="sm" disabled={selected.size === 0 && !bestellungOffen} onClick={() => setDialogOpen(true)}>
            {bestellungOffen ? "Offenen Bestellvorgang anzeigen" : `Bestellung erfassen (${selected.size} Artikel)`}
          </Button>
        </div>
      </div>

      {/* Dauerhaft eingehaengt, nicht bedingt gerendert: eine Live-Region, die zusammen mit
          ihrem Text in die Seite kommt, sagen verbreitete Vorleseprogramme nicht verlaesslich
          an — sie beobachten den Inhalt einer BESTEHENDEN Region. `empty:hidden` haelt die
          leere Zeile aus dem Bild. */}
      <p role="status" className="text-xs text-ink-2 empty:hidden">
        {dauertLaenger
          ? "Die Abfrage läuft noch — bei vielen Artikeln dauert sie merklich länger."
          : ""}
      </p>

      {/* `subtitle` haengt zusaetzlich an `!reachFehler`: scheitert ein NACHLADEN, haelt
          React Query die letzte erfolgreiche Antwort im Zwischenspeicher — `reach.data`
          ist dann gefuellt, obwohl die Kopfzeile darueber „Nicht geladen" sagt und die
          Tabelle weg ist. Ein Zeitstempel von vorhin sieht aus wie der von jetzt; er waere
          die einzige Stelle, die dem Nutzer noch Aktualitaet zusagt.
          (Befund Reviewer 1, MAJOR — am Code nachgeprueft.) */}
      <Panel
        title="Grundartikel"
        subtitle={
          reach.data && !reachFehler
            ? `Sortiert nach Dringlichkeit — Stand ${new Date(reach.data.generated_at).toLocaleTimeString("de-DE")}`
            : undefined
        }
        bodyClassName="p-0"
      >
        {reachFehler ? (
          // `role="alert"`: der Fehlschlag kommt nach langer Wartezeit und ohne Zutun des
          // Nutzers — wer nicht auf diesen Ausschnitt schaut, bekaeme ihn sonst nie mit.
          <div role="alert" className="text-sm text-coral py-8 text-center flex flex-col items-center gap-2">
            <span>{reachFehler}</span>
            <span className="text-ink-2 text-xs max-w-sm">
              Es wurde nichts geladen — deshalb steht hier keine Tabelle. Das ist keine Aussage
              ueber den Bestand.
            </span>
            {selected.size > 0 && (
              <span className="text-ink-2 text-xs max-w-sm">
                Die Auswahl ({selected.size}) bleibt bestehen — „Bestellung erfassen" ist
                weiter nutzbar.
              </span>
            )}
            {/* Waehrend des Versuchs gesperrt: mit Daten im Zwischenspeicher bleibt die
                Abfrage im Fehlerzustand, dieser Ausschnitt also sichtbar. Jeder weitere
                Klick startete eine zusaetzliche Abfrage — das Abbruchsignal wird bis zum
                `fetch` nicht durchgereicht, die vorherige laeuft also weiter. Genau die
                Last, gegen die `retry: false` steht. (Befund Reviewer 1, MAJOR.) */}
            <Button variant="outline" size="sm" disabled={reach.isFetching}
              onClick={() => void reach.refetch()}>
              {reach.isFetching ? "Versuch läuft…" : "Erneut versuchen"}
            </Button>
          </div>
        ) : !reach.isLoading && total === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
            <PackageSearch className="size-8 text-muted" />
            <p className="text-sm text-ink-2 max-w-sm">
              Keine Artikel für diese Filterkombination. Filter lockern oder „Ausreichend bestellte ausblenden"
              deaktivieren, falls alle Treffer schon ausreichend bestellt sind.
            </p>
          </div>
        ) : (
          <DataTable
            data={rows}
            columns={columns}
            rowKey={(row) => row.ga_id}
            isLoading={reach.isLoading}
            stickyHeader
            tableFixed
            emptyMessage="Keine Artikel auf dieser Seite."
            pagination={{
              hasMore: offset + rows.length < total,
              onNext: () => setOffset((o) => o + effectiveLimit),
              onPrev: () => setOffset((o) => Math.max(0, o - effectiveLimit)),
              canPrev: offset > 0,
              count: total,
            }}
          />
        )}
      </Panel>

      <OffeneBestellungenPanel />

      {/* Vormerkungen sind der Vorgaenger des Einkaufsmoduls. Sie verschwinden nicht
          (offene Vorgaenge muessen abschliessbar bleiben), treten aber einen Schritt
          zurueck: eingeklappt, ausdruecklich als Altbestand beschriftet. */}
      <details className="rounded-lg border border-line bg-surface">
        <summary className="cursor-pointer select-none px-4 py-3 text-sm text-ink-2 hover:text-ink">
          Vormerkungen (alt) — Vorgaenger des Einkaufsmoduls, nur noch zum Abschliessen
        </summary>
        <div className="px-4 pb-4">
          <OpenPurchaseNotesSection />
        </div>
      </details>

      <BestellungErfassenDialog
        includeDead={includeDead}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onPendingChange={setBestellungOffen}
        zeilen={selectedItems}
        mengen={orderQtyDraft}
        onAngelegt={() => {
          setSelected(new Map());
          setOrderQtyDraft({});
        }}
      />
    </div>
  );
}
